/**
 * PM Spec 20/05/2026 — Cockpit NPS / Feedback (admin/CFO)
 *
 * Vue CFO-friendly :
 *  - Score NPS en grand + breakdown promoters/passives/detractors
 *  - Trend journalier 30 jours (graphique recharts)
 *  - Liste verbatim des derniers feedbacks avec leurs avis
 *  - Bouton "Lancer une relance manuelle" (déclenche le scheduler J+1)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Heart, MessageCircle, Send, Loader2, Star, TrendingUp, RefreshCw, AlertCircle, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, ReferenceLine } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch { return iso; }
};

const npsColor = (n) => {
  if (n === null || n === undefined) return 'text-slate-400';
  if (n >= 50) return 'text-emerald-600';
  if (n >= 0) return 'text-amber-600';
  return 'text-red-600';
};

const npsLabel = (n) => {
  if (n === null || n === undefined) return 'N/A';
  if (n >= 70) return 'Excellent';
  if (n >= 50) return 'Très bon';
  if (n >= 30) return 'Correct';
  if (n >= 0) return 'À améliorer';
  return 'Critique';
};

export default function NpsCockpitPage() {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, f, l] = await Promise.all([
        axios.get(`${API}/api/admin/feedback-nps/stats?days=30`, {}),
        axios.get(`${API}/api/admin/feedback-nps/trend?days=30`, {}),
        axios.get(`${API}/api/admin/feedback-nps/list?limit=20`, {}),
        axios.get(`${API}/api/admin/feedback-nps/logs?limit=5`, {}),
      ]);
      setStats(s.data);
      setTrend(t.data.series || []);
      setFeedbacks(f.data.feedbacks || []);
      setLogs(l.data.logs || []);
    } catch (err) {
      toast({
        title: 'Impossible de charger le cockpit NPS',
        description: err.response?.data?.detail || 'Erreur serveur',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRunManual = async (dryRun = false) => {
    const msg = dryRun
      ? 'Lancer une SIMULATION de relance NPS J+1 ? Aucune action réelle ne sera effectuée (aucun email envoyé, aucune opération marquée).'
      : 'Lancer une relance NPS J+1 manuelle ? Les opérations livrées depuis >24h sans demande envoyée seront notifiées par email.';
    if (!window.confirm(msg)) return;
    setRunning(true);
    try {
      const url = `${API}/api/admin/feedback-nps/run${dryRun ? '?dry_run=true' : ''}`;
      const res = await axios.post(url, {}, {});
      toast({
        title: dryRun ? 'Simulation terminée' : 'Relance déclenchée',
        description: `${res.data.sent || 0} ${dryRun ? 'auraient été envoyés' : 'envoyés'} / ${res.data.candidates || 0} candidats / ${res.data.failed || 0} échecs`,
      });
      fetchAll();
    } catch (err) {
      toast({
        title: 'Action impossible',
        description: err.response?.data?.detail || 'Erreur serveur',
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const nps = stats?.nps;
  const breakdown = [
    { name: 'Promoteurs', value: stats?.promoters || 0, color: '#10b981' },
    { name: 'Passifs', value: stats?.passives || 0, color: '#f59e0b' },
    { name: 'Détracteurs', value: stats?.detractors || 0, color: '#ef4444' },
  ];

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="nps-cockpit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Award className="w-8 h-8 text-amber-600" />
            Cockpit Satisfaction Client
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Score NPS, trend 30 jours, et derniers retours clients · données live
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="nps-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
          </Button>
          <Button variant="outline" onClick={() => handleRunManual(true)} disabled={running} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="nps-dry-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertCircle className="w-4 h-4 mr-2" />}
            Simulation (dry-run)
          </Button>
          <Button onClick={() => handleRunManual(false)} disabled={running} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="nps-run-manual-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Lancer une relance manuelle
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* NPS gros */}
        <Card className="lg:col-span-2 border-amber-200" data-testid="nps-score-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Heart className="w-4 h-4 text-rose-500" />
              NPS sur 30 jours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-3">
              <span className={`text-7xl font-bold tabular-nums ${npsColor(nps)}`} data-testid="nps-score-value">
                {nps !== null && nps !== undefined ? nps : '–'}
              </span>
              <Badge className={`${nps >= 50 ? 'bg-emerald-100 text-emerald-800' : nps >= 0 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'} border-0`}>
                {npsLabel(nps)}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Net Promoter Score : %promoteurs − %détracteurs · échelle -100 à +100
            </p>
          </CardContent>
        </Card>

        <Card data-testid="nps-feedbacks-count-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-blue-500" /> Retours reçus
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-slate-900">{stats?.feedbacksCount || 0}</div>
            <p className="text-xs text-slate-500 mt-1">
              Note moyenne : <span className="font-semibold">{stats?.avgRating || 0} / 5</span>
            </p>
          </CardContent>
        </Card>

        <Card data-testid="nps-response-rate-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-500" /> Taux de réponse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-slate-900">{stats?.responseRate || 0}%</div>
            <p className="text-xs text-slate-500 mt-1">
              {stats?.requestsSent || 0} demandes envoyées
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown promoters / passives / detractors */}
      <Card data-testid="nps-breakdown-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">Répartition des avis (30j)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={70}>
            <BarChart data={breakdown} layout="vertical" margin={{ left: 80 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {breakdown.map((b, i) => <Cell key={i} fill={b.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Trend chart */}
      <Card data-testid="nps-trend-card">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-amber-600" /> Évolution du NPS sur 30 jours
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trend.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-8">Pas encore assez de données pour tracer un trend.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
                <YAxis domain={[-100, 100]} tick={{ fontSize: 11 }} />
                <Tooltip
                  labelFormatter={fmtDate}
                  formatter={(value, name) => [value, name === 'nps' ? 'NPS' : name]}
                />
                <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                <ReferenceLine y={50} stroke="#10b981" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="nps" stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Tabs : Feedbacks recents | Historique scheduler */}
      <Tabs defaultValue="feedbacks" className="w-full">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="feedbacks" data-testid="tab-feedbacks"><MessageCircle className="w-4 h-4 mr-1.5" /> Derniers retours</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs"><RefreshCw className="w-4 h-4 mr-1.5" /> Historique relances</TabsTrigger>
        </TabsList>

        <TabsContent value="feedbacks" className="space-y-3 mt-4" data-testid="feedbacks-tab">
          {feedbacks.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              Aucun retour client pour le moment.
            </div>
          ) : (
            feedbacks.map((f, idx) => {
              const rating = f.globalRating || 0;
              const tone = rating >= 5 ? 'border-emerald-200 bg-emerald-50/30' : rating <= 3 ? 'border-red-200 bg-red-50/30' : 'border-amber-200 bg-amber-50/30';
              return (
                <Card key={f.id || idx} className={tone} data-testid={`feedback-card-${idx}`}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center min-w-[40px]">
                        <Star className={`w-5 h-5 ${rating >= 5 ? 'text-emerald-500' : rating <= 3 ? 'text-red-500' : 'text-amber-500'}`} fill="currentColor" />
                        <span className="text-lg font-bold text-slate-900">{rating}/5</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="text-sm font-medium text-slate-800">
                            {f.operationNumber || f.operationId || 'Opération'}
                          </div>
                          <span className="text-xs text-slate-500">
                            {f.createdAt ? new Date(f.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                          </span>
                        </div>
                        {f.comment && (
                          <div className="text-sm text-slate-700 italic mt-1">« {f.comment} »</div>
                        )}
                        {f.criteriaRatings && (
                          <div className="flex flex-wrap gap-2 mt-2 text-xs">
                            {Object.entries(f.criteriaRatings).slice(0, 4).map(([k, v]) => (
                              <span key={k} className="bg-white border border-slate-200 rounded px-2 py-0.5">
                                {k} : <strong>{v}/5</strong>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="logs" className="space-y-2 mt-4" data-testid="logs-tab">
          {logs.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500">Aucune exécution récente du scheduler.</div>
          ) : (
            logs.map((log, idx) => (
              <Card key={idx} data-testid={`log-card-${idx}`}>
                <CardContent className="py-3 px-4 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    {log.failed > 0 ? <AlertCircle className="w-4 h-4 text-red-500" /> : <RefreshCw className="w-4 h-4 text-emerald-500" />}
                    <div>
                      <div className="text-sm font-medium">
                        {log.ranAt ? new Date(log.ranAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {log.candidates} candidats · {log.sent} envoyés · {log.failed} échecs · {log.skipped} ignorés
                      </div>
                    </div>
                  </div>
                  {log.failed > 0 && (
                    <Badge className="bg-red-100 text-red-700 border-0">
                      {log.failed} échec(s) SMTP
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
